import React, { useState, useContext } from "react";
import { AuthContext } from "../App";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_interpret-now/artifacts/8sijz41w_PassportTalk.png";

const Login = () => {
  const { t, i18n } = useTranslation();
  const { login, API } = useContext(AuthContext);
  const [isRegister, setIsRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: "traveler"
  });

  // Check if RTL
  const isRTL = i18n.language === 'ar' || document.dir === 'rtl';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (isRegister) {
        await axios.post(`${API}/auth/register`, formData);
        toast.success(t('auth.accountCreated', 'Account created! Please log in.'));
        setIsRegister(false);
      } else {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        login(response.data.token, response.data.user);
        toast.success(t('toast.welcomeBack'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.loginError', 'Authentication failed'));
    }
  };

  const togglePasswordVisibility = (e) => {
    e.preventDefault(); // Prevent form submission
    setShowPassword(!showPassword);
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1756364896901-b2be07f9bebc?crop=entropy&cs=srgb&fm=jpg&q=85)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/80 to-[#1e3a5f]/90 backdrop-blur-sm" />
      
      <div className="relative z-10 w-full max-w-md">
        <div className="backdrop-blur-xl bg-white/95 rounded-3xl shadow-2xl p-8 border border-white/20">
          <div className="flex flex-col items-center mb-8">
            <img 
              src={LOGO_URL} 
              alt="PassportTalk logo" 
              className="w-24 h-24 mb-4 object-contain"
            />
            <div className="flex items-center gap-2 mb-2">
              <h1 className="text-3xl font-bold text-[#0F172A] font-outfit" data-testid="app-title">{t('app.name')}</h1>
              <span className="px-2 py-1 bg-[#0EA5E9]/10 text-[#0EA5E9] text-xs font-semibold rounded-full">{t('app.beta')}</span>
            </div>
            <p className="text-[#475569] text-sm mt-2 font-medium">{t('app.tagline')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            {isRegister && (
              <div>
                <label className="block text-sm font-semibold text-[#334155] mb-2">{t('auth.name', 'Full Name')}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full h-12 bg-slate-50 border-2 border-slate-200 focus:border-[#0EA5E9] focus:ring-4 focus:ring-[#0EA5E9]/10 rounded-xl px-4 transition-all"
                  required
                  data-testid="name-input"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-[#334155] mb-2">{t('auth.email')}</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full h-12 bg-slate-50 border-2 border-slate-200 focus:border-[#0EA5E9] focus:ring-4 focus:ring-[#0EA5E9]/10 rounded-xl px-4 transition-all"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#334155] mb-2">{t('auth.password')}</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className={`w-full h-12 bg-slate-50 border-2 border-slate-200 focus:border-[#0EA5E9] focus:ring-4 focus:ring-[#0EA5E9]/10 rounded-xl px-4 transition-all ${isRTL ? 'pl-12 pr-4' : 'pr-12 pl-4'}`}
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className={`absolute top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-2 ${isRTL ? 'left-2' : 'right-2'}`}
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  data-testid="password-toggle"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {isRegister && (
              <div>
                <label className="block text-sm font-semibold text-[#334155] mb-2">{t('auth.role', 'I am a')}</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full h-12 bg-slate-50 border-2 border-slate-200 focus:border-[#0EA5E9] focus:ring-4 focus:ring-[#0EA5E9]/10 rounded-xl px-4 transition-all"
                  data-testid="role-select"
                >
                  <option value="traveler">{t('auth.traveler', 'Traveler')}</option>
                  <option value="agent">{t('auth.agent', 'Agent')}</option>
                  <option value="admin">{t('auth.admin', 'Admin')}</option>
                </select>
              </div>
            )}

            {isRegister && (
              <div className="text-xs text-slate-600 text-center px-2">
                {t('auth.consent')}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-gradient-to-r from-[#0EA5E9] to-[#06b6d4] hover:from-[#0284c7] hover:to-[#0891b2] text-white rounded-full h-12 font-bold shadow-lg shadow-[#0EA5E9]/30 transition-all active:scale-95 flex items-center justify-center gap-2"
              data-testid="submit-button"
            >
              {isRegister ? t('auth.signUp') : t('auth.signIn')}
              <ArrowRight className="w-5 h-5" />
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsRegister(!isRegister)}
              className="text-[#0EA5E9] hover:text-[#0284c7] text-sm font-semibold transition-colors"
              data-testid="toggle-auth-mode"
            >
              {isRegister ? t('auth.hasAccount') : t('auth.noAccount')}
            </button>
          </div>

          <div className="mt-6 p-4 bg-[#CCFBF1]/40 border border-[#99f6e4]/50 rounded-xl">
            <p className="text-xs text-[#334155] text-center font-medium">
              {t('landing.betaNotice')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
